/**
 * 三个核心配置场景的帮助内容（markdown 文本）
 * 由 HelpButton 引用展示
 */

export const HELP_INDICATOR = `
# 指标配置说明

**指标**是问卷的核心心理学/教育学测量结果，例如「抑郁倾向得分」「学习倦怠水平」。一份问卷可配置**多个指标**。

每个指标包含两部分：
- **计算公式**：决定如何把题目分数算成一个数值
- **结论规则**：把数值翻译成可读的判定文本（如"正常 / 需要关注 / 需要专业帮助"）

---

## 一、计算公式

| 公式类型 | 含义 | 何时使用 |
|---|---|---|
| **求和（SUM）** | \`Q1 + Q2 + Q3 + ...\` | 标准量表（PHQ-9、GAD-7 等）大多用求和 |
| **平均（AVERAGE）** | \`(Q1 + Q2 + ...) / N\` | 想要统一的"平均强度"分数 |
| **加权求和（WEIGHTED_SUM）** | \`w1·Q1 + w2·Q2 + ...\` | 各题重要性不同时（如某题权重 2，另一题权重 0.5） |

> 💡 选择"加权求和"后，每道题旁会出现**权重**输入框（默认 1.0）。

### 示例：抑郁自评量表 PHQ-9

- 公式：**求和**
- 涉及题目：Q1~Q9（每题 0-3 分）
- 取值范围：0-27 分

---

## 二、结论规则

规则按 \`orderNum\` 从小到大依次匹配，**返回第一个命中**的结论。

### 条件类型

| 条件类型 | 含义 | 配置 |
|---|---|---|
| **分数区间（SCORE_RANGE）** | 落在 [min, max] 之间 | min / max / 是否包含端点 |
| **分数等于（SCORE_EQUALS）** | 恰好等于某值 | 单一分数 |
| **文本等于（TEXT_EQUALS）** | 某 TEXT 题答案等于期望值 | 题目 + 期望文本 |
| **表达式（EXPRESSION）** | 任意布尔表达式 | 字符串表达式 |

---

### 示例 1：抑郁三级判定（分数区间）

| 规则序号 | 条件 | 结论 | 颜色 | 预警 |
|---|---|---|---|---|
| 0 | \`5 ≤ 得分 ≤ 10\` | 正常范围 | 🟢 绿色 | 否 |
| 1 | \`11 ≤ 得分 ≤ 17\` | 需要关注 | 🟡 橙色 | 否 |
| 2 | \`18 ≤ 得分 ≤ 25\` | 需要专业帮助 | 🔴 红色 | **是** |

### 示例 2：复合判断（表达式）

支持的运算：

- 数学：\`+ - * / % ()\`
- 比较：\`> < >= <= == !=\`
- 逻辑：\`&&（与） ||（或） !（非）\`

可引用的变量：

- \`SCORE\`：当前指标的得分（必须**大写**）
- \`Q1, Q2, ...\`：使用题目实际 ID（如 \`Q145\`）

\`\`\`
# 总分高且自伤倾向题（Q9）选了"是（值=1）"
SCORE >= 25 && Q9 == 1

# 任一项极端高分
Q1 == 5 || Q2 == 5 || Q3 == 5

# 平均分（3 题）大于 20
(Q1 + Q2 + Q3) / 3 > 20
\`\`\`

### 示例 3：文本判定（TEXT_EQUALS）

只能用于 **TEXT 题型**。例如有一道开放题"你对生活的感受？"，
设规则：

- 题目 = Q4，期望文本 = \`满意\` → 结论"生活满意度高"
- 题目 = Q4，期望文本 = \`一般\` → 结论"生活满意度一般"

---

## 三、预警标记

勾选规则中的「**预警**」复选框后，当用户答题命中此结论时，会自动在「**预警监控**」面板出现一条记录。
适合标记高危结论（自伤倾向、严重抑郁等），由心理老师及时跟进。

---

## 四、矩阵题单元格分数（高级）

如果指标包含矩阵题，可在指标编辑界面给**每个单元格**单独配分。
例如「学校倦怠量表」7×7 矩阵，可让正向题与反向题取相反分数。

> ⚠️ **常见坑**：表达式中变量名**大小写敏感**，\`SCORE\` 必须大写；矩阵题不能直接用 \`Q1\` 引用，请通过单元格分数累加进 \`SCORE\` 后再判断。
`;

export const HELP_DISPLAY_LOGIC = `
# 跳转逻辑（条件显示）说明

跳转逻辑让你**根据用户上一题的回答，动态显示或隐藏后续题目**。

---

## 一、典型场景

- **筛选问题**：第 3 题「你是否使用过本服务？」选「否」 → 跳过第 4-7 题
- **年龄分流**：年龄 ≥ 18 岁 → 显示成年人版本；< 18 岁 → 显示青少年版本
- **症状追问**：「过去两周是否出现失眠？」选「是」 → 显示"具体哪些时段失眠"题；选「否」 → 跳过

---

## 二、配置步骤

1. 进入问卷详情 → 「**跳转逻辑**」Tab
2. 点击「+ 添加规则」
3. 选择**触发题目**（哪道题的回答作为依据）
4. 选择**触发条件**
5. 选择**目标题目**（要显示 / 隐藏哪道题）
6. 多个条件可设置「与（AND）」或「或（OR）」连接

---

## 三、触发条件类型

| 题型 | 可用条件 |
|---|---|
| 单选 / 多选 | OPTION_SELECTED（选了某选项）/ OPTION_NOT_SELECTED / OPTIONS_CONTAINS_ANY / OPTIONS_CONTAINS_ALL / OPTIONS_NOT_CONTAINS |
| 文本题 | TEXT_EQUALS / TEXT_CONTAINS / TEXT_NOT_EMPTY / TEXT_REGEX_MATCH |
| 量表（滑块） | SLIDER_EQUALS / SLIDER_GREATER_THAN / SLIDER_LESS_THAN / SLIDER_RANGE |
| 矩阵题 | MATRIX_CELL_SELECTED / MATRIX_ROW_SELECTED / MATRIX_SCORE_RANGE / MATRIX_ALL_ANSWERED / MATRIX_ANY_ANSWERED |

---

## 四、示例

### 示例 1：性别分流

- 触发题目：Q1 性别（单选：男 / 女）
- 触发条件：\`OPTION_SELECTED\` = "男"
- 目标题目：Q5「兵役状态」 → 显示

### 示例 2：年龄区间

- 触发题目：Q2 年龄（量表 0-100）
- 触发条件：\`SLIDER_GREATER_THAN\` = 18
- 目标题目：Q8「就业情况」 → 显示

### 示例 3：症状追问

- 触发题目：Q3「最近两周是否失眠？」（单选：是 / 否）
- 触发条件：\`OPTION_SELECTED\` = "是"
- 目标题目：Q4「失眠的频率？」 → 显示

### 示例 4：组合条件（AND）

操作符选 \`AND\`，两条都成立时才显示：

- 条件 A：Q1 = 男
- 条件 B：Q2 ≥ 25

→ 显示 Q10「家庭责任评估」

---

## 五、注意事项

> ⚠️ **隐藏题目不进入指标计算**：被隐藏的题目分数不会算入任何指标。
>
> 💡 **从源头题目设计**：建议把分支类问题放在前面（性别、年龄、是否相关），便于后续题动态展示。
>
> 📌 **预览验证**：点击右上角「预览」按钮，可在用户视角下手动测试跳转效果。
`;

export const HELP_REPORT = `
# 大报告（综合报告）说明

「大报告」是问卷分析层面的**汇总报告**，把当前问卷的多个指标结果合并成一份机构 / 学校层面的可读文档。
区别于"个人报告"（每位学生 1 份），大报告通常是**整份问卷的统计汇总**。

---

## 一、模板与占位符

报告模板是富文本（HTML），通过**占位符**引用动态数据。占位符在最终预览时会被实际数据替换。

### 占位符类型

| 类型 | 说明 | 占位符 key 前缀 |
|---|---|---|
| **问卷指标** | 引用问卷已配置的指标 | \`IND_<id>\` |
| **系统占位符** | 平台预置（如参与人数、完成率） | \`SYS_\` |
| **自定义计算** | 自定义表达式 | \`COMP_\` |

### 数据字段

每个指标占位符可选不同的"取数方式"：

| 字段 | 含义 |
|---|---|
| \`AVG_SCORE\` | 该指标所有答卷的平均分 |
| \`MIN_SCORE\` / \`MAX_SCORE\` | 最低 / 最高分 |
| \`RESPONDENT_COUNT\` | 参与人数 |
| \`CONCLUSION_COUNT\` / \`CONCLUSION_PERCENTAGE\` | 某个结论命中的人数 / 百分比（需选具体规则） |
| \`CONCLUSION_PIE_CHART\` / \`CONCLUSION_BAR_CHART\` | 结论分布饼图 / 柱状图 |

---

## 二、自动生成默认模板

首次进入「大报告」配置且模板为空时，可点击「**生成默认模板**」让平台**根据当前问卷的题目和指标**自动构造一份初稿，包括：

- 问卷标题与描述
- 概览（已分发 / 已完成 / 完成率）
- 每个指标的平均分、最高分、最低分、参与人数
- 每个指标的结论分布饼图（如有结论规则）

> 💡 默认模板生成后你**可以自由编辑**——增删段落、调整文字、修改样式，最后点击保存。
>
> ⚠️ 如果问卷**没有指标**，默认模板会提示先配置指标，建议**先到指标配置页添加指标**再回来生成。

---

## 三、自定义模板示例

\`\`\`html
<h1>{{questionnaire.title}} · 学期评估报告</h1>

<h2>一、回收概况</h2>
<p>本次问卷共发放 {{SYS_DISTRIBUTED_COUNT}} 份，
   实际完成 {{SYS_RESPONDENT_COUNT}} 份，
   完成率 {{SYS_COMPLETION_RATE}}。</p>

<h2>二、抑郁倾向分布</h2>
<p>平均分：{{IND_3.AVG_SCORE}}</p>
<p>{{IND_3.CONCLUSION_PIE_CHART}}</p>

<h2>三、需重点关注</h2>
<p>本次问卷中"需要专业帮助"判定占比 {{IND_3.CONCLUSION_PERCENTAGE}}，
   建议班主任与心理老师跟进。</p>
\`\`\`

---

## 四、规则与限制

> ⚠️ **问卷必须有题目**：空问卷无法保存模板，请先在「问题列表」添加至少 1 道题。
>
> 💡 **修改指标会影响模板**：如果删除了某个指标，模板里引用该指标的占位符会变成空值，请检查并更新。
>
> 📌 **预览数据来源**：点击「预览」按钮后展示的数值来自**已发布且已收集到答卷**的数据；草稿状态可能为 0。
`;
